// frontend/src/pages/MonthlyMembersOverviewPage.jsx
// CONSOLIDATED VERSION: Shows combined loan balances, single repayment per type
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function fmt(n, dp=2){
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function asYyyyMm(d){
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth()+1,'0').padStart(2,'0');
  return `${y}-${m}`;
}

function addMonths(yyyyMm, delta){
  const [y, m] = yyyyMm.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return asYyyyMm(d);
}

export default function MonthlyMembersOverviewPage(){
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(asYyyyMm(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [filterText, setFilterText] = useState('');

  useEffect(() => { 
    loadMonthlyData(); 
  }, [selectedMonth]);

  async function loadMonthlyData(){
    try{
      setErr('');
      
      setLoading(true);
      
      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(`${API_URL}/reports/monthly-overview?month=${selectedMonth}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.message || 'Failed to load monthly overview');
      
      setData(j.data);
    } catch(e){ 
      setErr(e.message || 'Failed to load'); 
    } finally{ 
      setLoading(false); 
    }
  }
  
  function goToPreviousMonth(){
    setSelectedMonth(prev => addMonths(prev, -1));
  }

  function goToNextMonth(){
    setSelectedMonth(prev => addMonths(prev, 1));
  }
  
  async function exportCSV() {
    if (!selectedMonth) return;
    const token = localStorage.getItem('hopeSaccoToken');
    const url = `${API_URL}/reports/monthly-processing/export?month=${selectedMonth}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        alert(errorData.error || 'Export failed');
        return;
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `monthly-overview-${selectedMonth}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export CSV');
    }
  }

  function viewMemberStatement(memberId){
    navigate(`/admin/reports/member-statement?memberId=${memberId}&month=${selectedMonth}`);
  }

  // Filter members
  let membersToShow = [];
  if (data?.members) {
    const term = (filterText || '').toLowerCase();
    membersToShow = [...data.members]
      .filter(m => {
        if (!term) return true;
        const name = (m.fullName || '').toLowerCase();
        const code = (m.memberId || '').toLowerCase();
        return name.includes(term) || code.includes(term);
      });
  }

  return (
    <div className="container" style={{ maxWidth: '98%' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ color:'#4b3ff6', marginBottom: 4 }}>Monthly Members Overview</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#666' }}>
            <span onClick={() => navigate('/admin/dashboard')} style={{ cursor: 'pointer', color: '#4b3ff6', textDecoration: 'none' }}>
              Dashboard
            </span>
            <span>›</span>
            <span style={{ color: '#333' }}>Monthly Members Overview</span>
          </div>
          <p style={{ color:'#666', marginTop:4 }}>
            View all members&apos; financial data for a single month
          </p>
        </div>
      </div>

      {err && <div className="alert alert-danger" style={{ marginTop: 10 }}>{err}</div>}

      <div style={{ display:'flex', gap:12, marginTop:16, alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button onClick={goToPreviousMonth} style={{ padding:'8px 16px', fontSize:'14px', cursor:'pointer' }}>
            ◄ Prev
          </button>
          
          <div>
            <label style={{ marginRight:8 }}>Month:</label>
            <input type="month" value={selectedMonth} onChange={e=>setSelectedMonth(e.target.value)} style={{ padding:'8px', fontSize:'14px' }} />
          </div>
          
          <button onClick={goToNextMonth} style={{ padding:'8px 16px', fontSize:'14px', cursor:'pointer' }}>
            Next ►
          </button>
          
        </div>

        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          <button onClick={loadMonthlyData} disabled={loading} style={{ marginRight:8 }} title="Refresh current month">
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button onClick={exportCSV} disabled={!data}>
            Export CSV
          </button>
        </div>
      </div>

      {loading && (
        <div style={{ marginTop:20, textAlign:'center', color:'#666' }}>
          Loading data for all members...
        </div>
      )}

      {data && !loading && (
        <div style={{ marginTop:20 }}>
          <div style={{ background:'#f0f0f0', padding:'10px', borderRadius:'4px', marginBottom:10, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <strong>Viewing: {data.month}</strong>
              <span style={{ marginLeft:16, color:'#666' }}>
                Total Members: {membersToShow.length} / {data.members?.length || 0}
              </span>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="text"
                placeholder="Filter by name or Member ID"
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                style={{ padding: '6px 10px', fontSize: 13, borderRadius: 4, border: '1px solid #ccc', minWidth: 220 }}
              />
              {filterText && (
                <button type="button" onClick={() => setFilterText('')} style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4 }}>
                  Ã¢Å“â€¢
                </button>
              )}
            </div>

            <div style={{ color:'#4b3ff6', fontWeight:'bold' }}>
              Grand Total Deductions: KES {fmt(data.totals?.grandTotalDeductions || 0, 2)}
            </div>
          </div>

          <div style={{ overflowX:'auto' }}>
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <table className="table table-striped" style={{ fontSize:'13px', width: '100%' }}>
                <thead style={{ position:'sticky', top:0, background:'#fff', zIndex: 2 }}>
                <tr>
                  <th style={{ minWidth:'100px' }}>Member ID</th>
                  <th style={{ minWidth:'150px' }}>Name</th>
                  <th className="text-right" style={{ minWidth:'80px' }}>Shares</th>
                  <th className="text-right" style={{ minWidth:'100px' }}>Shares Contri.</th>

                  {/* NORMAL LOAN - Consolidated */}
                  <th className="text-right" style={{ minWidth:'100px' }}>Loan</th>
                  <th className="text-right" style={{ minWidth:'90px' }}>Loan Int.</th>
                  <th className="text-right" style={{ minWidth:'90px' }}>Repayment</th>

                  {/* SCHOOL LOAN - Consolidated */}
                  <th className="text-right" style={{ minWidth:'90px' }}>School</th>
                  <th className="text-right" style={{ minWidth:'90px' }}>School Int.</th>
                  <th className="text-right" style={{ minWidth:'90px' }}>School Rep.</th>

                  {/* EMERGENCY LOAN - Consolidated */}
                  <th className="text-right" style={{ minWidth:'90px' }}>Emerg.</th>
                  <th className="text-right" style={{ minWidth:'90px' }}>Emerg. Int.</th>
                  <th className="text-right" style={{ minWidth:'90px' }}>Emerg. Rep.</th>

                  <th className="text-right" style={{ minWidth:'90px' }}>Appl. Fees</th>
                  <th className="text-right" style={{ minWidth:'110px' }}><strong>Total Deductions</strong></th>
                </tr>
              </thead>
              <tbody>
                {membersToShow.map((member, i) => {
                  const m = member.monthData;
                  const hasActiveLoans = m.loanBalance > 0 || m.schoolFeesLoan > 0 || m.emergencyLoan > 0;

                  return (
                    <tr 
                      key={i}
                      onClick={() => viewMemberStatement(member.memberId)}
                      style={{ cursor:'pointer', background: hasActiveLoans ? '#fff9e6' : 'transparent' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0f8ff'}
                      onMouseLeave={e => e.currentTarget.style.background = hasActiveLoans ? '#fff9e6' : 'transparent'}
                      title="Click to view full member statement"
                    >
                      <td><strong>{member.memberId}</strong></td>
                      <td>{member.fullName}</td>
                      <td className="text-right">{fmt(m.shares,0)}</td>
                      <td className="text-right">{fmt(m.sharesContribution,2)}</td>

                      <td className="text-right">{fmt(m.loanBalance,2)}</td>
                      <td className="text-right">{fmt(m.loanInterest,2)}</td>
                      <td className="text-right">{fmt(m.loanRepayment,0)}</td>

                      <td className="text-right">{fmt(m.schoolFeesLoan,2)}</td>
                      <td className="text-right">{fmt(m.schoolFeesInterest,2)}</td>
                      <td className="text-right">{fmt(m.schoolFeesRepayment,0)}</td>

                      <td className="text-right">{fmt(m.emergencyLoan,2)}</td>
                      <td className="text-right">{fmt(m.emergencyLoanInterest,2)}</td>
                      <td className="text-right">{fmt(m.emergencyLoanRepayment,0)}</td>

                      <td className="text-right">{fmt(m.applicationFees,2)}</td>
                      <td className="text-right" style={{ fontWeight:'bold' }}>
                        {fmt(m.totalDeductions,2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr style={{ background:'#e8f4f8', fontWeight:'bold', fontSize:'14px' }}>
                  <td colSpan="2"><strong>TOTALS (All Members)</strong></td>
                  <td className="text-right">{fmt(data.totals.totalShares,2)}</td>
                  <td className="text-right">{fmt(data.totals.totalSharesContributed,2)}</td>

                  <td className="text-right">{fmt(data.totals.totalLoanBalance,2)}</td>
                  <td className="text-right">{fmt(data.totals.totalLoanInterest,2)}</td>
                  <td className="text-right">{fmt(data.totals.totalLoanRepayments,2)}</td>

                  <td className="text-right">{fmt(data.totals.totalSchoolLoan,2)}</td>
                  <td className="text-right">{fmt(data.totals.totalSchoolInterest,2)}</td>
                  <td className="text-right">{fmt(data.totals.totalSchoolRepayment,2)}</td>

                  <td className="text-right">{fmt(data.totals.totalEmergencyLoan,2)}</td>
                  <td className="text-right">{fmt(data.totals.totalEmergencyInterest,2)}</td>
                  <td className="text-right">{fmt(data.totals.totalEmergencyRepayment,2)}</td>

                  <td className="text-right">{fmt(data.totals.totalApplicationFees,2)}</td>
                  <td className="text-right" style={{ fontSize:'15px' }}>
                    {fmt(data.totals.grandTotalDeductions,2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          </div>
        </div>
      )}

      {data && data.members?.length === 0 && (
        <div style={{ marginTop:40, textAlign:'center', color:'#999', padding:'40px' }}>
          <p style={{ fontSize:'18px' }}>No members found for {data.month}</p>
          <p>Try selecting a different month or add members to the system.</p>
        </div>
      )}
    </div>
  );
}